import { Injectable } from "@angular/core";
import { ResourceService } from "./resource.service";
import { EMAIL_SIZE_POLICY } from "./email-size.policy";

export interface UiError {
    kind: string;
    title: string;
    message: string;
    traceId?: string;
    details?: any;
    btnTitle?: string;
}

export function isAuthUiError(error: any): error is UiError {
  return error?.kind === "AuthUiError";
}

export function isEmailSizeUiError(error: any): error is UiError {
  return error?.kind === "EmailSizeError";
}

export function isUnsafeAttachmentUiError(error: any): error is UiError {
  return error?.kind === "UnsafeAttachmentError";
}

export function isTimeoutUiError(error: any): error is UiError {
  return error?.kind === "TimeoutError";
}

export function isGraphApiUiError(error: any): error is UiError {
  return error?.kind === "GraphApiError";
}

@Injectable({
  providedIn: "root",
})
export class UIErrorFactory {
  private constructor(
    private resourceService: ResourceService
  ) {}

  public createSOAuthInterrupted(traceId: string, err?: any): UiError {
    return {
      kind: "AuthUiError",
      title: "Outlook is no longer connected to SuperOffice",
      message: "Click Reconnect below to sign in again and continue archiving emails.",
      traceId,
      details: err ? err : new Error("SuperOffice login failed"),
      btnTitle: "Reconnect"
    };
  }

  public createGraphTransientFailure(traceId: string, err?: any): UiError {
    return {
      kind: "GraphApiError",
      title: this.resourceService.localizeResources.GRAPH_API_ERROR_TITLE_SOFO,
      message: this.resourceService.localizeResources.GRAPH_API_ERROR_MESSAGE_SOFO,
      traceId,
      details: err ? err : new Error("Graph API transient failure"),
    };
  }

  public createSOffice365APIAuthInterrupted(traceId: string, err?: any): UiError {
    return {
      kind: "AuthUiError",
      title: "Connection to Microsoft lost",
      message: "SuperOffice for Outlook lost its connection to Microsoft. Close the SuperOffice for Outlook panel and open it again to reconnect.",
      traceId,
      details: err ? err : new Error("Microsoft 365 login failed")
    };
  }

  public createCaeFailure(traceId: string, err?: any): UiError {
    return {
      kind: "AuthUiError",
      title: "Connection to Microsoft lost",
      message: "SuperOffice for Outlook lost its connection to Microsoft. Close the SuperOffice for Outlook panel and open it again to reconnect.",
      traceId,
      details: err ? err : new Error("Conditional access evaluation error")
    };
  }

  public createEmailSizeFailure(traceId: string, err?: any): UiError {
    return {
      kind: "EmailSizeError",
      title: "Email too large",
      message: `This email exceeds the archive file size limit of ${EMAIL_SIZE_POLICY.MAX_MB} MB and cannot be archived.`,
      traceId,
      details: err ? err : new Error("Email too large"),
    };
  }

  public createRequestSizeFailure(traceId: string): UiError {
    return {
      kind: "RequestSizeError",
      title: this.resourceService.localizeResources.REQUEST_EMAIL_TOO_LARGE_TITLE,
      message: this.resourceService.localizeResources.REQUEST_EMAIL_TOO_LARGE_MESSAGE,
      traceId,
    };
  }

  public createAppointmentLoadFailure(traceId: string, err?: any): UiError {
    return {
      ...this.createGraphTransientFailure(traceId, err ?? new Error("Failed to load appointment SuperOffice link data")),
      btnTitle: this.resourceService.localizeResources.BUTTON_RELOAD,
    };
  }

  public createTimeoutFailure(traceId: string): UiError {
    return {
      kind: "TimeoutError",
      title: this.resourceService.localizeResources.TIMEOUT_ERROR_TITLE,
      message: this.resourceService.localizeResources.TIMEOUT_ERROR_MESSAGE,
      traceId,
    };
  }

  public createUnsafeAttachmentFailure(traceId: string, err?: any): UiError {
    return {
      kind: "UnsafeAttachmentError",
      title: "Unsafe email attachment",
      message: this.resourceService.localizeResources.CANNOT_ARCHIVE_EMAIL_UNSAFE_ATTACHMENT,
      traceId,
      details: err ? err : new Error("Unsafe email attachment cannot be archived"),
    };
  }
}
